import { CsvData } from "./useCsvData";

/**
 * 간소화된 CSV 파서
 * @description 더 안정적이고 간단한 CSV 파싱을 위한 유틸리티
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-15
 */
export class SimpleCsvParser {
  /**
   * CSV 파일을 간단하게 파싱
   * @param file CSV 파일
   * @returns 파싱된 결과
   */
  static async parseFile(file: File): Promise<{
    success: boolean;
    data: CsvData[];
    errors: string[];
    totalLines: number;
  }> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;

          console.log("[SIMPLE_CSV_START]", {
            fileName: file.name,
            fileSize: file.size,
            textLength: text?.length || 0,
            firstChars: text?.substring(0, 100) || "empty",
          });

          if (!text || text.trim() === "") {
            resolve({
              success: false,
              data: [],
              errors: ["파일이 비어있습니다."],
              totalLines: 0,
            });
            return;
          }

          // 다양한 줄바꿈 문자 처리
          const lines = text
            .replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n")
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

          console.log("[SIMPLE_CSV_LINES]", {
            totalLines: lines.length,
            firstLine: lines[0] || "empty",
            secondLine: lines[1] || "empty",
            thirdLine: lines[2] || "empty",
          });

          if (lines.length === 0) {
            resolve({
              success: false,
              data: [],
              errors: ["유효한 데이터 라인이 없습니다."],
              totalLines: 0,
            });
            return;
          }

          // 헤더 라인 확인
          const headerLine = lines[0];
          const headerFields = this.splitCsvLine(headerLine);

          console.log("[SIMPLE_CSV_HEADER]", {
            headerLine,
            headerFields,
            fieldCount: headerFields.length,
          });

          // 최소 2개 컬럼은 있어야 함 (Email, First name)
          if (headerFields.length < 2) {
            resolve({
              success: false,
              data: [],
              errors: [
                "CSV 파일에 최소 2개 컬럼(Email, First name)이 필요합니다.",
              ],
              totalLines: lines.length,
            });
            return;
          }

          // 헤더에서 컬럼 위치 찾기
          const emailIndex = this.findColumnIndex(headerFields, [
            "email",
            "mail",
          ]);
          const firstNameIndex = this.findColumnIndex(headerFields, [
            "first",
            "name",
            "firstname",
          ]);
          const lastNameIndex = this.findColumnIndex(headerFields, [
            "last",
            "lastname",
            "surname",
          ]);
          const phoneIndex = this.findColumnIndex(headerFields, [
            "phone",
            "tel",
            "mobile",
          ]);

          console.log("[SIMPLE_CSV_MAPPING]", {
            emailIndex,
            firstNameIndex,
            lastNameIndex,
            phoneIndex,
          });

          // 필수 컬럼 검증
          if (emailIndex === -1 && firstNameIndex === -1) {
            // 헤더가 없는 경우 순서대로 매핑
            const dataLines = lines;
            const mappedData = this.parseDataLines(dataLines, {
              emailIndex: 0,
              firstNameIndex: 1,
              lastNameIndex: 2,
              phoneIndex: 3,
            });

            resolve({
              success: true,
              data: mappedData.validData,
              errors: mappedData.errors,
              totalLines: lines.length,
            });
            return;
          }

          if (emailIndex === -1 || firstNameIndex === -1) {
            resolve({
              success: false,
              data: [],
              errors: ["Email과 First name 컬럼을 찾을 수 없습니다."],
              totalLines: lines.length,
            });
            return;
          }

          // 데이터 라인 처리 (헤더 제외)
          const dataLines = lines.slice(1);
          const mappedData = this.parseDataLines(dataLines, {
            emailIndex,
            firstNameIndex,
            lastNameIndex,
            phoneIndex,
          });

          resolve({
            success: mappedData.errors.length === 0,
            data: mappedData.validData,
            errors: mappedData.errors,
            totalLines: lines.length,
          });
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Unknown parsing error";
          console.error("[SIMPLE_CSV_ERROR]", {
            error,
            errorMessage,
            fileName: file.name,
          });

          resolve({
            success: false,
            data: [],
            errors: [`파일 파싱 오류: ${errorMessage}`],
            totalLines: 0,
          });
        }
      };

      reader.onerror = () => {
        resolve({
          success: false,
          data: [],
          errors: ["파일 읽기 실패"],
          totalLines: 0,
        });
      };

      // UTF-8로 읽기 시도
      reader.readAsText(file, "utf-8");
    });
  }

  /**
   * CSV 라인을 필드로 분할
   */
  private static splitCsvLine(line: string): string[] {
    // 간단한 CSV 분할 (콤마 기준)
    const fields = line.split(",").map((field) => field.trim());

    // 따옴표 제거
    return fields.map((field) => {
      if (
        (field.startsWith('"') && field.endsWith('"')) ||
        (field.startsWith("'") && field.endsWith("'"))
      ) {
        return field.slice(1, -1);
      }
      return field;
    });
  }

  /**
   * 컬럼 인덱스 찾기
   */
  private static findColumnIndex(
    headerFields: string[],
    keywords: string[]
  ): number {
    return headerFields.findIndex((field) => {
      const normalizedField = field.toLowerCase().trim();
      return keywords.some((keyword) => normalizedField.includes(keyword));
    });
  }

  /**
   * 데이터 라인들 파싱
   */
  private static parseDataLines(
    dataLines: string[],
    columnMapping: {
      emailIndex: number;
      firstNameIndex: number;
      lastNameIndex: number;
      phoneIndex: number;
    }
  ): {
    validData: CsvData[];
    errors: string[];
  } {
    const validData: CsvData[] = [];
    const errors: string[] = [];
    const seenEmails = new Set<string>();
    const duplicateEmails: string[] = [];

    dataLines.forEach((line, index) => {
      const lineNumber = index + 2; // 헤더 다음부터

      try {
        const fields = this.splitCsvLine(line);

        const email =
          fields[columnMapping.emailIndex]?.trim().toLowerCase() || "";
        const firstName = fields[columnMapping.firstNameIndex]?.trim() || "";
        const lastName =
          columnMapping.lastNameIndex >= 0
            ? fields[columnMapping.lastNameIndex]?.trim() || ""
            : "";
        const phone =
          columnMapping.phoneIndex >= 0
            ? fields[columnMapping.phoneIndex]?.trim() || ""
            : "";

        // 🔍 상세 필드 디버깅 로그
        console.log(`[CSV_LINE_${lineNumber}]`, {
          email: email || "(empty)",
          firstName: firstName || "(empty)",
          lastName: lastName || "(empty)",
          phone: phone || "(empty)",
          rawFields: fields.map((f) => f || "(empty)"),
        });

        // 필수 필드 검증
        if (!email) {
          const errorMsg = `Line ${lineNumber}: Email is required (found: "${email}")`;
          console.error(`[VALIDATION_ERROR] ${errorMsg}`);
          errors.push(errorMsg);
          return;
        }

        // ✅ First name이 없어도 허용 (필수 조건에서 제거)
        // First name 검증 제거됨

        // 이메일 형식 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          const errorMsg = `Line ${lineNumber}: Invalid email format (found: "${email}")`;
          console.error(`[VALIDATION_ERROR] ${errorMsg}`);
          errors.push(errorMsg);
          return;
        }

        // 🔥 중복 이메일 검사 및 자동 제거
        if (seenEmails.has(email)) {
          duplicateEmails.push(email);
          // 에러가 아닌 경고로 처리 (자동 제거)
          console.warn(
            `[DUPLICATE_REMOVED] Line ${lineNumber}: Duplicate email "${email}" - automatically removed`
          );
          return;
        }

        // 이메일을 세트에 추가
        seenEmails.add(email);

        validData.push({
          email,
          firstName: firstName || "Unknown", // First name이 없으면 기본값 설정
          lastName: lastName || undefined,
          phone: phone || undefined,
        });
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        errors.push(`Line ${lineNumber}: Parse error - ${errorMessage}`);
      }
    });

    // 🎯 중복 이메일 처리 결과 로그
    if (duplicateEmails.length > 0) {
      console.log("[DUPLICATE_SUMMARY]", {
        duplicateCount: duplicateEmails.length,
        uniqueEmails: validData.length,
        totalProcessed: dataLines.length,
        duplicateList: duplicateEmails.slice(0, 10), // 처음 10개만 로그
        hasMore: duplicateEmails.length > 10,
      });

      // 중복 제거 알림을 errors 대신 별도 처리
      errors.push(
        `✅ 중복된 이메일 ${duplicateEmails.length}개가 자동으로 제거되었습니다. 최종 ${validData.length}개의 고유한 이메일이 처리됩니다.`
      );
    }

    // 📊 최종 처리 결과 요약
    console.log("[CSV_PROCESSING_SUMMARY]", {
      totalLines: dataLines.length,
      validData: validData.length,
      errors: errors.length,
      duplicatesRemoved: duplicateEmails.length,
      successRate: `${((validData.length / dataLines.length) * 100).toFixed(
        1
      )}%`,
      rejectedReasons: {
        missingEmail: errors.filter((e) => e.includes("Email is required"))
          .length,
        invalidEmailFormat: errors.filter((e) =>
          e.includes("Invalid email format")
        ).length,
        duplicates: duplicateEmails.length,
        parseErrors: errors.filter((e) => e.includes("Parse error")).length,
      },
    });

    return { validData, errors };
  }
}
